import React from 'react';
import { Global, css } from '@emotion/react';

import { Input, Modal, RadioGroup } from 'design-system';

import {
  Description,
  FooterActions,
  FooterButton,
  FormatSection,
  MODAL_EMAIL_CLASS,
  EmailModalTitle,
  Summary,
  SummaryAmount,
  SummaryLeft,
  secondCopyEmailModalGlobalStyles,
} from './styles';

export type DocumentFormat = 'XML' | 'PDF';

export type SecondCopyModalMode = 'download' | 'email';

export type SecondCopyModalProps = {
  open: boolean;
  mode: SecondCopyModalMode;
  itemCount: number;
  totalAmount: string;
  format?: DocumentFormat;
  onFormatChange?: (format: DocumentFormat) => void;
  onClose: () => void;
  onSubmit: () => void;
  loading?: boolean;
  submitDisabled?: boolean;
  showFormatSelection?: boolean;
  title?: React.ReactNode;
  description?: React.ReactNode;
  email?: string;
  onEmailChange?: (value: string) => void;
  itemLabelSingular?: string;
  itemLabelPlural?: string;
  submitLabel?: string;
  formatLabel?: string;
  formatOptions?: DocumentFormat[];
  className?: string;
};

const DEFAULT_FORMAT_OPTIONS: DocumentFormat[] = ['XML', 'PDF'];

const DocumentIcon = (): JSX.Element => (
  <svg viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10.5 2.25H4.5C3.94772 2.25 3.5 2.69772 3.5 3.25V14.75C3.5 15.3023 3.94772 15.75 4.5 15.75H13.5C14.0523 15.75 14.5 15.3023 14.5 14.75V6.25L10.5 2.25Z"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinejoin="round"
    />
    <path
      d="M10.5 2.25V6.25H14.5"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinejoin="round"
    />
  </svg>
);

const getDefaultTitle = (mode: SecondCopyModalMode): React.ReactNode =>
  mode === 'download' ? (
    'Baixar 2ª via'
  ) : (
    <EmailModalTitle>
      Enviar segunda via por<span>e-mail</span>
    </EmailModalTitle>
  );

const getDefaultSubmitLabel = (mode: SecondCopyModalMode): string =>
  mode === 'download' ? 'Baixar' : 'Enviar';

const SecondCopyModal = ({
  open,
  mode,
  itemCount,
  totalAmount,
  format = 'PDF',
  onFormatChange,
  onClose,
  onSubmit,
  loading = false,
  submitDisabled = false,
  showFormatSelection = true,
  title,
  description,
  email = '',
  onEmailChange,
  itemLabelSingular = 'item',
  itemLabelPlural = 'itens',
  submitLabel,
  formatLabel = 'Em que formato você deseja?',
  formatOptions = DEFAULT_FORMAT_OPTIONS,
  className,
}: SecondCopyModalProps): JSX.Element => {
  const itemLabel = itemCount === 1 ? itemLabelSingular : itemLabelPlural;
  const resolvedTitle = title ?? getDefaultTitle(mode);
  const resolvedSubmitLabel = submitLabel ?? getDefaultSubmitLabel(mode);
  const isEmailMode = mode === 'email';
  const modalClassName = [isEmailMode ? MODAL_EMAIL_CLASS : null, className]
    .filter(Boolean)
    .join(' ');

  const footerButtons = isEmailMode ? (
    <>
      <FooterButton
        type="button"
        $variant="solid"
        disabled={loading || submitDisabled}
        onClick={onSubmit}
      >
        {resolvedSubmitLabel}
      </FooterButton>
      <FooterButton type="button" $variant="outline" onClick={onClose}>
        Cancelar
      </FooterButton>
    </>
  ) : (
    <>
      <FooterButton type="button" $variant="outline" onClick={onClose}>
        Cancelar
      </FooterButton>
      <FooterButton
        type="button"
        $variant="solid"
        disabled={loading || submitDisabled}
        onClick={onSubmit}
      >
        {resolvedSubmitLabel}
      </FooterButton>
    </>
  );

  return (
    <>
      {isEmailMode && (
        <Global
          styles={css`
            ${secondCopyEmailModalGlobalStyles}

            @media (max-width: 768px) {
              .second-copy-modal-email {
                padding: 16px;
              }

              .second-copy-modal-email [role='dialog'] > div:nth-of-type(2) {
                padding-bottom: 16px;
              }

              .second-copy-modal-email [role='dialog'] > div:nth-of-type(3) {
                padding-top: 0;
              }

              .second-copy-modal-email [role='dialog'] > div:first-of-type {
                align-items: flex-start;
                gap: 12px;
                padding-top: 20px;
              }

              .second-copy-modal-email [role='dialog'] h2 {
                flex: 1;
                min-width: 0;
                font-size: 16px;
                line-height: 1.25;
                hyphens: none;
              }
            }
          `}
        />
      )}
      <Modal
      open={open}
      title={resolvedTitle}
      onClose={onClose}
      className={modalClassName || undefined}
      footer={
        <FooterActions $emailMode={isEmailMode}>{footerButtons}</FooterActions>
      }
    >
      {description && <Description>{description}</Description>}

      {mode === 'email' && onEmailChange && (
        <Input
          label="E-mail"
          type="email"
          value={email}
          fullWidth
          shape="pill"
          onChange={(event) => onEmailChange(event.currentTarget.value)}
        />
      )}

      {showFormatSelection && formatOptions.length > 0 && (
        <FormatSection>
          <RadioGroup
            label={formatLabel}
            value={format}
            options={formatOptions.map((option) => ({
              value: option,
              label: option,
            }))}
            onChange={(value) => onFormatChange?.(value as DocumentFormat)}
          />
        </FormatSection>
      )}

      <Summary $emailMode={isEmailMode}>
        <SummaryLeft>
          <DocumentIcon />
          <strong>
            {itemCount} {itemLabel}
          </strong>
        </SummaryLeft>
        <SummaryAmount>{totalAmount}</SummaryAmount>
      </Summary>
    </Modal>
    </>
  );
};

export default SecondCopyModal;
